"use client";

import Image from "next/image";
import { useEffect, useRef, useMemo } from "react";
import { gsap } from "@/lib/gsap";
import { getWindowDimensions, batchRead } from "@/lib/helper";

interface Logo {
  src: string;
  alt: string;
}

interface LogoTickerData {
  content?: {
    title?: string;
    logos?: Logo[];
  };
}

interface LogoTickerClientProps {
  data: LogoTickerData;
}

export default function LogoTickerClient({ data }: LogoTickerClientProps) {
  const sectionRef = useRef<HTMLDivElement>(null);
  const headingRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const duplicatedLogos = useMemo(() => {
    const logos = data?.content?.logos || [];

    return [...logos, ...logos, ...logos];
  }, [data?.content?.logos]);

  useEffect(() => {
    if (!sectionRef.current || !containerRef.current) return;

    const ctx = gsap.context(() => {
      const images = containerRef.current?.querySelectorAll("img");
      if (!images || images.length === 0) return;

      const imagePromises = Array.from(images).map((img) => {
        if (img.complete) return Promise.resolve();
        return new Promise((resolve) => {
          img.onload = () => resolve(null);
          img.onerror = () => resolve(null);
        });
      });

      Promise.all(imagePromises).then(() => {
        // Batch all reads together using requestAnimationFrame
        requestAnimationFrame(() => {
          const singleSetWidth = batchRead(() => {
            let width = 0;
            const logoCount = (data?.content?.logos || []).length;

            for (let i = 0; i < logoCount; i++) {
              if (images[i]) {
                width += images[i].offsetWidth;
              }
            }
            return width;
          });

          const { width } = getWindowDimensions();
          const gapSize = width >= 768 ? 64 : 40;
          const totalWidth =
            singleSetWidth + gapSize * (data?.content?.logos || []).length;

          if (totalWidth === 0) return;

          const timeline = gsap.timeline({
            scrollTrigger: {
              trigger: sectionRef.current,
              start: "top 75%",
              end: "top 50%",
              toggleActions: "play none none reverse",
            },
          });

          timeline
            .fromTo(
              headingRef.current,
              { opacity: 0, y: 40 },
              {
                opacity: 1,
                y: 0,
                duration: 1,
                ease: "power2.out",
              }
            )
            .fromTo(
              containerRef.current,
              { opacity: 0, y: 30, scale: 0.94 },
              {
                opacity: 1,
                y: 0,
                scale: 1,
                duration: 1,
                ease: "cubic-bezier(0.25, 0.46, 0.45, 0.94)",
              },
              "-=0.4"
            );

          gsap.set(containerRef.current, { x: 0 });

          const animation = gsap.to(containerRef.current, {
            x: -totalWidth,
            duration: 15,
            ease: "none",
            repeat: -1,
            scrollTrigger: {
              trigger: sectionRef.current,
              start: "top 80%",
              end: "bottom 20%",
              toggleActions: "play pause resume reverse",
            },
          });

          animation.eventCallback("onRepeat", () => {
            gsap.set(containerRef.current, { x: 0 });
          });
        });
      });
    }, sectionRef);

    return () => ctx.revert();
  }, [data?.content?.logos]);

  return (
    <div ref={sectionRef} className="py-12 overflow-hidden">
      <div className="">
        <h2
          ref={headingRef}
          className="text-center text-base font-semibold text-theme-secondary mb-8 opacity-0"
        >
          {data?.content?.title}
        </h2>
        <div className="relative flex overflow-hidden ">
          <div
            ref={containerRef}
            className="opacity-0 whitespace-nowrap flex items-center gap-x-10 md:gap-x-12 [&_img]:max-h-20 [&_img]:max-w-40! [&_img]:object-contain  will-change-transform"
          >
            {duplicatedLogos.map(
              (logo, index) =>
                logo.src && (
                  <Image
                    width={72}
                    height={48}
                    key={`logo-${index}`}
                    src={logo.src}
                    alt={logo.alt}
                    quality={75}
                    loading="lazy"
                    sizes="72px"
                    className="h-16 w-auto object-contain  transition-all duration-300 aspect-3/2 grayscale hover:grayscale-0 opacity-60 hover:opacity-100"
                  />
                )
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
